import { SKU_INVALID_CHAR_PATTERN, SKU_INVALID_CHAR_PATTERN_GLOBAL, SUFFIXED_SKU_PATTERN } from '@/constants'

/**
 * SKU character rules.
 *
 * SKUs may contain ONLY `A–Z`, `a–z`, and `0–9`. Everything else — spaces,
 * punctuation, symbols, emoji, accented/non-ASCII letters — is invalid and is
 * hard-rejected at entry (never allowed into the field) and blocks listing.
 *
 * Case is preserved as entered (lowercase is valid); empty is out of scope for
 * this rule — required/optional behaviour is governed elsewhere.
 *
 * Patterns + error strings live in `@/constants` (settings).
 */

/**
 * The seller-entered part of a stored SKU: everything before the first hyphen.
 *
 * The SKU inputs never let a hyphen through, so a hyphen can only have been put
 * there by the API when it appended `-<base36 card id>` (`47690-13oej6`).
 * Anything from the first hyphen onward is therefore machine-generated and not
 * the seller's to answer for. No hyphen — the whole value is theirs.
 */
export const skuBase = (value) => String(value ?? '').split('-')[0]

/**
 * True when `value` is a valid SKU. Empty/nullish is considered valid here —
 * this only governs character content, not whether a SKU is required.
 *
 * Only `skuBase` is judged. Checking the whole stored value flagged the API's
 * own `-<id>` suffix as an invalid character and blocked listing on cards whose
 * SKUs were fine — the reason this is scoped to the entered part.
 *
 * The trade-off: for a legacy SKU that already contained a hyphen, whatever
 * follows it goes unchecked (`AB-CD EF` passes on the strength of `AB`). That
 * is the deliberate direction — the failure mode being fixed is blocking valid
 * listings, and nothing a seller can type today reaches that case.
 */
export const isValidSku = (value) => {
  const base = skuBase(value)

  return base === '' || !SKU_INVALID_CHAR_PATTERN.test(base)
}

/** Strip every disallowed character, returning an alphanumeric-only string. */
export const sanitizeSku = (value) => String(value ?? '').replace(SKU_INVALID_CHAR_PATTERN_GLOBAL, '')

/**
 * Paste guard for the SKU inputs.
 *
 * The `input` handlers already strip disallowed characters, but that lets the
 * raw clipboard text land in the field for a frame first, and (with `v-model`)
 * pushes it through the model on its way out. This cancels the native paste
 * and inserts the sanitized text at the caret itself, so a disallowed
 * character is never in the field at all — the same hard-reject the keydown
 * guard gives typing.
 *
 * `setRangeText` bypasses `maxlength`, so the insertion is clamped to whatever
 * room the field has left.
 *
 * A synthetic `input` event is dispatched afterwards so the existing listeners
 * (`v-model`, the debounced store update) run exactly as they do for typing —
 * only with an already-clean value.
 *
 * Returns `true` when the clipboard held disallowed characters, so the caller
 * can flash the inline error.
 */
export const pasteSanitizedSku = (event) => {
  const input = event.target

  event.preventDefault()

  const pasted = event.clipboardData?.getData('text') ?? ''
  const cleaned = sanitizeSku(pasted)

  const start = input.selectionStart ?? input.value.length
  const end = input.selectionEnd ?? input.value.length
  const maxLength = input.maxLength > 0 ? input.maxLength : Infinity
  const room = maxLength - (input.value.length - (end - start))

  input.setRangeText(cleaned.slice(0, Math.max(room, 0)), start, end, 'end')
  input.dispatchEvent(new Event('input', { bubbles: true }))

  return cleaned !== pasted
}

/**
 * Read-only base-36 card id that Batch Details shows after the SKU
 * (`47690 - 13oej6`) so the seller can see the full SKU that reaches the
 * marketplaces.
 *
 * The API now bakes that suffix into `sku` itself for new batches, so appending
 * it unconditionally rendered `47690-13oej6 - 13oej6`. Returns '' whenever the
 * value already carries a suffix, so it is never doubled.
 *
 * Detection is by shape (`SUFFIXED_SKU_PATTERN`), not by comparing to this
 * card's own id: the API has emitted SKUs carrying a neighbouring card's id
 * (`47696-13oekj` on the card whose id is `13oeki`), and re-appending our
 * locally computed id there reads as a second suffix just the same.
 */
export const skuIdSuffix = (sku, id) => {
  if (id == null) return ''

  const value = String(sku ?? '').trim()

  if (value && SUFFIXED_SKU_PATTERN.test(value)) return ''

  return id.toString(36)
}
